import { affiliationService } from "app/services/affiliation.service";
import { PayoutStatus } from "types/affiliation.types";
import { PayoutsStatistics } from "features/affiliation/common/types";
import {
  PAYOUTS_STATISTIC_TITLE_TO_STATUS,
  USD_PRECISION,
} from "features/affiliation/common/constants";
import { toFixed } from "@shared/utils/numbers";
import { useContext, useState } from "react";
import { openMessage } from "utils/misc";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces, PayoutMethodSource } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service.ts";

const usePayouts = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { analytics } = useContext(AnalyticsContext);

  const payoutsHistoryQuery = affiliationService.useGetPayoutsHistoryQuery();
  const payoutMethodsQuery =
    sharedLib.sharedApi.settings.useGetPayoutMethodsQuery({
      source: PayoutMethodSource.AFFILIATION,
    });
  const payouts = payoutsHistoryQuery.data;
  const hasPayoutMethod = !!payoutMethodsQuery.data?.length;
  const showEmptyState = !payouts?.length;

  const [isCreatePayoutModalVisible, setIsCreatePayoutModalVisible] =
    useState(false);

  const sumAmountFilteredByStatus = (status: PayoutStatus) => {
    return payouts
      ?.filter((payout) => payout.status === status)
      .reduce((sum, payout) => sum + payout.amount, 0);
  };

  const countPayoutsFilteredByStatus = (status: PayoutStatus) => {
    return payouts?.filter((payout) => payout.status === status).length;
  };

  const getStatisticValue = (title: PayoutsStatistics) => {
    if (
      title === PayoutsStatistics.PENDING_PAYOUTS ||
      title === PayoutsStatistics.PROCESSED_PAYOUTS
    ) {
      return countPayoutsFilteredByStatus(
        PAYOUTS_STATISTIC_TITLE_TO_STATUS[title]
      );
    } else if (
      title === PayoutsStatistics.AMOUNT_AWAITING_APPROVAL ||
      title === PayoutsStatistics.PAID_TO_DATE
    ) {
      return `$${toFixed(
        sumAmountFilteredByStatus(PAYOUTS_STATISTIC_TITLE_TO_STATUS[title]),
        USD_PRECISION
      )}`;
    }
  };

  const openCreatePayoutModal = () => {
    setIsCreatePayoutModalVisible(true);
  };
  const closeCreatePayoutModal = () => {
    setIsCreatePayoutModalVisible(false);
  };

  const onCreatePayoutClick = () => {
    analytics?.affiliationNewPayoutClicked({});
    if (hasPayoutMethod) openCreatePayoutModal();
    else {
      openMessage(t("payouts.create_payout_method"), "warning");
    }
  };

  return {
    payoutsHistoryQuery,
    payoutMethodsQuery,
    getStatisticValue,
    isCreatePayoutModalVisible,
    onCreatePayoutClick,
    closeCreatePayoutModal,
    showEmptyState,
    hasPayoutMethod,
  };
};

export default usePayouts;
